import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { IconComponent } from '../lib/types';

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

export interface CalloutProps {
  tone?: CalloutTone | undefined;
  title: string;
  children?: ReactNode;
  /** Recovery action. An error state that offers no way forward is a dead end,
   *  which PRD §15 explicitly rules out. */
  action?: ReactNode;
  className?: string | undefined;
}

const TONES: Record<CalloutTone, { className: string; Icon: IconComponent }> = {
  info: { className: 'border-info/30 bg-info-soft text-info', Icon: Info },
  success: { className: 'border-success/30 bg-success-soft text-success', Icon: CircleCheck },
  warning: { className: 'border-warning/30 bg-warning-soft text-warning', Icon: TriangleAlert },
  danger: { className: 'border-danger/30 bg-danger-soft text-danger', Icon: CircleAlert },
};

/**
 * An inline message block — the error and warning states PRD §15 requires be
 * designed rather than left to a generic toast.
 *
 * `danger` and `warning` announce themselves: they render as `role="alert"`,
 * because they normally appear in response to something the user just did.
 * `info` and `success` stay silent, since announcing every confirmation
 * interrupts more than it helps.
 */
export function Callout({ tone = 'info', title, children, action, className }: CalloutProps) {
  const { className: toneClassName, Icon } = TONES[tone];
  const assertive = tone === 'danger' || tone === 'warning';

  return (
    <div
      role={assertive ? 'alert' : undefined}
      className={cn('flex gap-3 rounded-md border p-3 text-sm', toneClassName, className)}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-medium">{title}</p>
        {children === undefined ? null : <div className="text-fg-muted">{children}</div>}
        {action === undefined ? null : <div className="mt-1">{action}</div>}
      </div>
    </div>
  );
}
